// TSE Workspace UI kit — small, dependency-free React components that render
// the PDF design language (see src/styles/tse-ui.css).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../pages/Header';
import Topbar from './Topbar';
import { uploadAttachment, openAttachment, wsAPI } from '../services/api';
import { notify } from '../utils/notify';

// ─── Formatting ──────────────────────────────────────────────────────────────
export const money = (n, opts = {}) => {
  const v = Number(n) || 0;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: opts.decimals ?? 0, minimumFractionDigits: opts.decimals ?? 0 }).format(v);
};
export const num = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
export const pad2 = (n) => String(Number(n) || 0).padStart(2, '0');
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const toDate = (d) => {
  if (!d) return null;
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
};
export const fmtDate = (d) => {
  const x = toDate(d);
  return x ? `${pad2(x.getDate())} ${MON[x.getMonth()]} ${x.getFullYear()}` : '—';
};
export const fmtDateShort = (d) => {
  const x = toDate(d);
  return x ? `${pad2(x.getDate())} ${MON[x.getMonth()]}` : '—';
};
export const fmtDateTime = (d) => {
  const x = toDate(d);
  if (!x) return '—';
  return `${fmtDate(x)}, ${pad2(x.getHours())}:${pad2(x.getMinutes())}`;
};
export const fmtRange = (a, b) => {
  const x = toDate(a); const y = toDate(b);
  if (!x && !y) return '—';
  if (!y || !x) return fmtDate(x || y);
  if (x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth()) {
    return x.getDate() === y.getDate() ? fmtDate(x) : `${pad2(x.getDate())} – ${pad2(y.getDate())} ${MON[y.getMonth()]} ${y.getFullYear()}`;
  }
  if (x.getFullYear() === y.getFullYear()) return `${pad2(x.getDate())} ${MON[x.getMonth()]} – ${pad2(y.getDate())} ${MON[y.getMonth()]} ${y.getFullYear()}`;
  return `${fmtDate(x)} – ${fmtDate(y)}`;
};
export const isoDate = (d) => {
  const x = toDate(d);
  if (!x) return '';
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
};
export const today = () => isoDate(new Date());
export const timeAgo = (d) => {
  const x = toDate(d);
  if (!x) return '';
  const s = Math.max(0, (Date.now() - x.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  return fmtDate(x);
};
export const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
export const sizeLabel = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
export const cls = (...a) => a.filter(Boolean).join(' ');

// ─── Data hooks ──────────────────────────────────────────────────────────────
// useAsync(fn, deps) → { data, loading, error, reload, setData }
export function useAsync(fn, deps = [], { initial = null, silent = false } = {}) {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seq = useRef(0);
  const run = useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    try {
      const d = await fn();
      if (id === seq.current) { setData(d); setError(null); }
    } catch (e) {
      if (id === seq.current) {
        setError(e);
        if (!silent && e?.status !== 403) notify.error(e.message || 'Failed to load');
      }
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [run]);
  return { data, loading, error, reload: run, setData };
}

// Shared, cached lookups (people directory, projects)
const cache = {};
async function cached(key, loader, ttl = 60000) {
  const hit = cache[key];
  if (hit && Date.now() - hit.at < ttl) return hit.promise;
  const promise = loader().catch((e) => { delete cache[key]; throw e; });
  cache[key] = { at: Date.now(), promise };
  return promise;
}
export const clearCache = (key) => { if (key) delete cache[key]; else Object.keys(cache).forEach((k) => delete cache[k]); };
export function useDirectory() {
  const { data } = useAsync(() => cached('directory', () => wsAPI.directory().then((d) => d.users || [])), [], { initial: [], silent: true });
  return data || [];
}
export function useProjects() {
  const { data } = useAsync(() => cached('projects', () => wsAPI.projects({ status: 'ACTIVE' }).then((d) => d.projects || [])), [], { initial: [], silent: true });
  return data || [];
}
export function useCostCenters() {
  const { data } = useAsync(() => cached('costCenters', () => wsAPI.costCenters().then((d) => d.costCenters || [])), [], { initial: [], silent: true });
  return data || [];
}
export function useCostCategories() {
  const { data } = useAsync(() => cached('costCategories', () => wsAPI.costCategories().then((d) => d.costCategories || [])), [], { initial: [], silent: true });
  return data || [];
}

// Read ?id=... etc from the URL. Tracks react-router navigation too, so a
// link to the same page with a different ?id= (bell, inbox, search) updates.
export function useQueryParam(name) {
  const location = useLocation();
  return new URLSearchParams(location.search).get(name);
}

// ─── Page frame ──────────────────────────────────────────────────────────────
export function PageShell({ section, children, footerNote }) {
  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <Topbar section={section} />
          <section className="tse-page">{children}</section>
        </main>
      </div>
    </>
  );
}

export function PageHead({ title, subtitle, crumbs, eyebrow, actions, badge, icon, children }) {
  return (
    <div className="tse-page-head">
      <div style={{ minWidth: 0 }}>
        {crumbs && (
          <div className="tse-crumbs">
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 && <i className="bi bi-chevron-right sep" />}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
              </React.Fragment>
            ))}
          </div>
        )}
        {eyebrow && <div className="tse-eyebrow">{eyebrow}</div>}
        <h1 className="tse-title">
          {icon && <i className={`bi ${icon}`} />}
          {title}
          {badge}
        </h1>
        {subtitle && <div className="tse-subtitle">{subtitle}</div>}
        {children}
      </div>
      {actions && <div className="tse-head-actions">{actions}</div>}
    </div>
  );
}

// ─── Primitives ──────────────────────────────────────────────────────────────
export function Stat({ icon, tone = 'blue', label, value, foot, tint, to, onClick, compact, valueTone, loading }) {
  const inner = (
    <>
      {icon && <div className={`ic ${tone}`}><i className={`bi ${icon}`} /></div>}
      <div style={{ minWidth: 0 }}>
        <div className="lbl">{label}</div>
        <div className={cls('val', valueTone)}>{loading ? <span className="tse-skel" style={{ display: 'block', width: 50, height: 26, marginTop: 4 }} /> : value}</div>
        {foot && <div className="foot">{foot}</div>}
      </div>
    </>
  );
  const c = cls('tse-stat', tint && `tint-${tint}`, compact && 'compact');
  if (to) return <Link to={to} className={c}>{inner}</Link>;
  if (onClick) return <button type="button" className={c} onClick={onClick}>{inner}</button>;
  return <div className={c}>{inner}</div>;
}

export function Card({ title, sub, icon, actions, navy, children, className, bodyClass = '', style, headStyle }) {
  return (
    <div className={cls('tse-card', navy && 'navy', className)} style={style}>
      {(title || actions) && (
        <div className="tse-card-head" style={headStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>{icon && <i className={`bi ${icon} h-icon`} />}{title}</div>
            {sub && <div className="sub">{sub}</div>}
          </div>
          {actions && <div className="tse-row" style={{ gap: 8 }}>{actions}</div>}
        </div>
      )}
      {bodyClass === null ? children : <div className={cls('tse-card-body', bodyClass)}>{children}</div>}
    </div>
  );
}

export function Badge({ tone = 'gray', icon, children, lg, pill, title }) {
  return <span className={cls('tse-badge', tone, lg && 'lg', pill && 'pill')} title={title}>{icon && <i className={`bi ${icon}`} />}{children}</span>;
}

// Generic status → badge tone
const STATUS_TONES = {
  approved: 'approved', active: 'approved', published: 'approved', closed: 'approved', completed: 'approved', delivered: 'approved', settled: 'paid', paid: 'paid',
  pending: 'pending', 'pending manager': 'pending', submitted: 'pending', 'under review': 'pending', 'in review': 'review', 'awaiting approval': 'pending',
  draft: 'draft', 'not started': 'draft', cancelled: 'draft', inactive: 'draft',
  rejected: 'rejected', returned: 'rejected', error: 'rejected', expired: 'rejected', revoked: 'draft',
  open: 'info', assigned: 'review', planned: 'review', 'in progress': 'review', scheduled: 'green', verify: 'green',
};
export function toneFor(status = '') {
  const s = String(status).toLowerCase().replace(/_/g, ' ');
  if (STATUS_TONES[s]) return STATUS_TONES[s];
  if (s.startsWith('pending')) return 'pending';
  if (s.includes('approved')) return 'approved';
  if (s.includes('reject')) return 'rejected';
  return 'gray';
}
export const titleCase = (s = '') => String(s).toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
export function StatusBadge({ status, label, icon, lg }) {
  return <Badge tone={toneFor(status)} icon={icon} lg={lg}>{label || titleCase(status)}</Badge>;
}

export function Tabs({ tabs, value, onChange, variant, style }) {
  return (
    <div className={cls('tse-tabs', variant)} style={style} role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={value === t.key} className={value === t.key ? 'active' : ''} onClick={() => onChange(t.key)}>
          {t.label}
          {t.count !== undefined && t.count !== null && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
export function Seg({ tabs, value, onChange }) {
  return (
    <div className="tse-seg">
      {tabs.map((t) => <button key={t.key} type="button" className={value === t.key ? 'active' : ''} onClick={() => onChange(t.key)}>{t.label}</button>)}
    </div>
  );
}

export function Field({ label, required, hint, error, children, inline, className, style }) {
  return (
    <div className={cls('tse-field', inline && 'inline', className)} style={style}>
      {label && <label>{label}{required && <span className="req">*</span>}</label>}
      {children}
      {hint && <div className="hint">{hint}</div>}
      {error && <div className="err">{error}</div>}
    </div>
  );
}
export const Input = React.forwardRef(({ className, ...p }, ref) => <input ref={ref} className={cls('tse-input', className)} {...p} />);
export function Textarea({ className, rows = 3, ...p }) { return <textarea className={cls('tse-input', className)} rows={rows} {...p} />; }
export function Select({ options, placeholder, className, children, ...p }) {
  return (
    <select className={cls('tse-input', className)} {...p}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options ? options.map((o) => (typeof o === 'object'
        ? <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
        : <option key={o} value={o}>{o}</option>)) : children}
    </select>
  );
}
export function MoneyInput({ value, onChange, ...p }) {
  return (
    <div className="tse-input-group">
      <span className="pre">₹</span>
      <input className="tse-input" type="number" min="0" step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value)} {...p} />
    </div>
  );
}
export function Check({ checked, onChange, label, disabled, style }) {
  return (
    <label className="tse-check" style={style}>
      <input type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
export function Switch({ checked, onChange, label }) {
  return (
    <label className="tse-switch">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="track" />
      {label && <span>{label}</span>}
    </label>
  );
}

export function Note({ tone = 'info', icon, title, children, big, style, className }) {
  const ic = icon || { info: 'bi-info-circle-fill', warn: 'bi-exclamation-circle-fill', danger: 'bi-exclamation-triangle-fill', ok: 'bi-check-circle-fill', plain: 'bi-info-circle' }[tone];
  return (
    <div className={cls('tse-note', tone, big && 'big', className)} style={style}>
      <i className={`bi ${ic}`} />
      <div>{title && <div className="t">{title}</div>}{children}</div>
    </div>
  );
}

export function Empty({ icon = 'bi-inbox', title = 'Nothing here yet', sub, action }) {
  return (
    <div className="tse-empty">
      <i className={`bi ${icon}`} />
      <div className="t">{title}</div>
      {sub && <div className="s">{sub}</div>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  );
}
export function Loading({ label = 'Loading…' }) {
  return <div className="tse-loading"><span className="tse-spinner" />{label}</div>;
}
export function Spin() { return <span className="tse-spinner sm" />; }

export function Avatar({ name, url, size, tone }) {
  return <span className={cls('tse-avatar', size, tone)}>{url ? <img src={url} alt="" /> : initials(name)}</span>;
}

// ─── Steppers & flows ────────────────────────────────────────────────────────
export function Stepper({ steps, current = 0, onStep, stacked }) {
  return (
    <div className={cls('tse-stepper', stacked && 'stacked')}>
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="line" />}
          <div className={cls('step', i === current && 'active', i < current && 'done')} style={{ cursor: onStep ? 'pointer' : 'default' }} onClick={() => onStep && onStep(i)}>
            <span className="num">{i < current ? <i className="bi bi-check-lg" /> : i + 1}</span>
            <span className="lbl">{s}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
// nodes: [{ icon, label, sub, state: 'active'|'done'|'' }]
export function Flow({ nodes, small, plainArrows }) {
  return (
    <div className={cls('tse-flow', small && 'small')}>
      {nodes.map((n, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className={cls('arrow', plainArrows && 'plain')} />}
          <div className={cls('node', n.state)}>
            <div className="circle"><i className={`bi ${n.icon}`} /></div>
            <div className="t">{n.label}</div>
            {n.sub && <div className="s">{n.sub}</div>}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
export function DotFlow({ nodes }) {
  return (
    <div className="tse-dotflow">
      {nodes.map((n, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="arrow">→</div>}
          <div className={cls('node', n.state)}><span className="dot" /><span className="t">{n.label}</span></div>
        </React.Fragment>
      ))}
    </div>
  );
}
// items: [{ mark: '1' | icon, state: 'done'|'current'|'ring'|'rejected'|'returned'|'skipped'|'', title, sub, subTone, meta }]
export function VFlow({ items }) {
  return (
    <div className="tse-vflow">
      {items.map((it, i) => (
        <div className="item" key={i}>
          <div className={cls('mark', it.state)}>
            {it.state === 'done' ? <i className="bi bi-check-lg" /> : it.state === 'rejected' ? <i className="bi bi-x-lg" /> : it.state === 'current' ? null : it.state === 'returned' ? <i className="bi bi-arrow-return-left" /> : it.mark}
          </div>
          <div className="body">
            <div className="t">{it.title}</div>
            {it.sub && <div className={cls('s', it.subTone)}>{it.sub}</div>}
          </div>
          {it.meta && <div className="meta">{it.meta}</div>}
        </div>
      ))}
    </div>
  );
}

// Approval routing for a generic workspace request (stages from backend)
export function RequestRouting({ request, showRequestor = true, draftStages }) {
  if (!request) {
    return <VFlow items={(draftStages || []).map((s, i) => ({ mark: i + 1, state: i === 0 ? '' : 'ring', title: s.label, sub: s.description }))} />;
  }
  const items = [];
  if (showRequestor) {
    items.push({
      mark: <i className="bi bi-person" />, state: request.status === 'DRAFT' ? 'ring' : 'done', title: 'Requestor',
      sub: request.status === 'DRAFT' ? 'Draft' : 'Submitted', meta: request.submittedAt ? <>{request.requesterName}<br />{fmtDateTime(request.submittedAt)}</> : request.requesterName,
    });
  }
  (request.stages || []).forEach((s, i) => {
    const map = { APPROVED: 'done', PENDING: 'current', REJECTED: 'rejected', RETURNED: 'returned', SKIPPED: 'skipped', WAITING: 'ring' };
    const subText = { APPROVED: 'Approved', PENDING: 'Pending', REJECTED: 'Rejected', RETURNED: 'Returned for clarification', SKIPPED: 'Not required', WAITING: s.description };
    items.push({
      mark: i + 1, state: map[s.status] || 'ring', title: s.label,
      sub: s.status === 'WAITING' ? s.description : subText[s.status], subTone: s.status === 'PENDING' ? 'pending' : undefined,
      meta: s.actedAt ? <>{s.actedByName}<br />{fmtDateTime(s.actedAt)}</> : (s.approverName || (s.status === 'PENDING' ? (s.approverRoles || []).join(' / ') : '–')),
    });
  });
  return <VFlow items={items} />;
}

// ─── Modal ───────────────────────────────────────────────────────────────────
export function Modal({ open, title, sub, onClose, size, footer, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="tse-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className={cls('tse-modal', size)} role="dialog" aria-modal="true">
        <div className="tse-modal-head">
          <div><div className="t">{title}</div>{sub && <div className="s">{sub}</div>}</div>
          {onClose && <button type="button" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg" /></button>}
        </div>
        <div className="tse-modal-body">{children}</div>
        {footer && <div className="tse-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ─── Kebab menu ──────────────────────────────────────────────────────────────
export function Kebab({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return undefined;
    const on = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', on);
    return () => document.removeEventListener('mousedown', on);
  }, [open]);
  const visible = items.filter((i) => i && !i.hidden);
  if (!visible.length) return null;
  return (
    <div className="tse-kebab" ref={ref}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} aria-label="Actions"><i className="bi bi-three-dots-vertical" /></button>
      {open && (
        <div className="tse-dropdown">
          {visible.map((it, i) => (
            <button key={i} type="button" className={it.danger ? 'danger' : ''} disabled={it.disabled} onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}>
              {it.icon && <i className={`bi ${it.icon}`} />}{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Pagination ──────────────────────────────────────────────────────────────
export function usePaged(items, pageSize = 10) {
  const [page, setPage] = useState(1);
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safe = Math.min(page, pages);
  useEffect(() => { setPage(1); }, [total]);
  return { page: safe, setPage, pages, total, pageSize, rows: items.slice((safe - 1) * pageSize, safe * pageSize) };
}
export function Pager({ page, pages, total, pageSize, setPage, label = 'records' }) {
  if (!total) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const list = [];
  for (let p = 1; p <= pages; p += 1) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 1) list.push(p);
    else if (list[list.length - 1] !== '…') list.push('…');
  }
  return (
    <div className="tse-pager">
      <span>Showing {start} – {end} of {total} {label}</span>
      {pages > 1 && (
        <div className="pages">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous"><i className="bi bi-chevron-left" /></button>
          {list.map((p, i) => (p === '…' ? <span key={`e${i}`} style={{ padding: '4px 3px' }}>…</span> : <button key={p} type="button" className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>))}
          <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next"><i className="bi bi-chevron-right" /></button>
        </div>
      )}
    </div>
  );
}

// Sort helper: const s = useSort(rows, 'createdAt', 'desc'); <th onClick={()=>s.by('x')}>
export function useSort(rows, initialKey, initialDir = 'asc') {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState(initialDir);
  const sorted = [...(rows || [])].sort((a, b) => {
    if (!key) return 0;
    const get = typeof key === 'function' ? key : (x) => x?.[key];
    const va = get(a); const vb = get(b);
    if (va === vb) return 0;
    if (va === undefined || va === null) return 1;
    if (vb === undefined || vb === null) return -1;
    const r = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
    return dir === 'asc' ? r : -r;
  });
  const by = (k) => { if (k === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setKey(k); setDir('asc'); } };
  const icon = (k) => (k === key ? (dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-chevron-expand');
  return { rows: sorted, by, icon, key, dir };
}
export function Th({ sort, k, children, className, style }) {
  if (!sort) return <th className={className} style={style}>{children}</th>;
  return <th className={cls('tse-th-sort', className)} style={style} onClick={() => sort.by(k)}>{children}<i className={`bi ${sort.icon(k)}`} /></th>;
}

// ─── Files ───────────────────────────────────────────────────────────────────
const MAX_BYTES = 10 * 1024 * 1024;
export function FileDrop({ onFiles, accept = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png', multiple = true, title = 'Choose files', sub = 'PDF, DOC, XLS, JPG (Max 10 MB each)', inline, button, disabled, icon }) {
  const input = useRef();
  const [drag, setDrag] = useState(false);
  const pick = (list) => {
    const files = Array.from(list || []);
    const ok = files.filter((f) => f.size <= MAX_BYTES);
    if (ok.length < files.length) notify.warn('Files larger than 10 MB were skipped');
    if (ok.length) onFiles(multiple ? ok : ok.slice(0, 1));
  };
  return (
    <div
      className={cls('tse-drop', inline && 'inline', drag && 'drag')}
      onClick={() => !disabled && input.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (!disabled) pick(e.dataTransfer.files); }}
      role="button" tabIndex={0}
    >
      <input ref={input} type="file" hidden accept={accept} multiple={multiple} onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      <i className={`bi ${icon || (inline ? 'bi-paperclip' : 'bi-cloud-arrow-up')} ic`} />
      <div>
        <div className="t" style={inline ? { fontSize: '0.846rem', fontWeight: 400 } : undefined}>
          {inline ? <><span className="tse-link">{title}</span> or drag and drop</> : title}
        </div>
        <div className="s" style={inline ? { margin: 0 } : undefined}>{sub}</div>
        {button && <span className="tse-btn" style={{ marginTop: 4 }}>{button}</span>}
      </div>
    </div>
  );
}
export function FileChip({ file, onRemove, onOpen }) {
  const open = onOpen || (file?.s3Key ? () => openAttachment(file).catch(() => notify.error('Could not open file')) : null);
  return (
    <div className="tse-file">
      <i className="bi bi-paperclip ic" />
      <div style={{ minWidth: 0, cursor: open ? 'pointer' : 'default' }} onClick={open || undefined}>
        <div className="n">{file.fileName || file.name}</div>
        <div className="z">{sizeLabel(file.size)}{file.uploading ? ' · uploading…' : ''}</div>
      </div>
      {onRemove && <button type="button" className="x" onClick={onRemove} aria-label="Remove"><i className="bi bi-x-lg" /></button>}
    </div>
  );
}
// Uploads File objects to S3 (presigned) and returns attachment descriptors
export async function uploadFiles(files, folder = 'workspace') {
  const out = [];
  for (const f of files) {
    // eslint-disable-next-line no-await-in-loop
    const a = await uploadAttachment(f, folder);
    out.push({ ...a, fileType: f.type, size: f.size });
  }
  return out;
}

// ─── Prompts ─────────────────────────────────────────────────────────────────
export async function promptText({ title, label = 'Remarks', placeholder = '', required = true, confirmText = 'Submit', color = '#1479bd', input = 'textarea', value = '' }) {
  const res = await Swal.fire({
    title, input, inputLabel: label, inputPlaceholder: placeholder, inputValue: value,
    showCancelButton: true, confirmButtonText: confirmText, confirmButtonColor: color, cancelButtonColor: '#6b7280',
    inputValidator: (v) => (required && !String(v || '').trim() ? `${label} is required` : undefined),
  });
  return res.isConfirmed ? (res.value || '') : null;
}
export async function confirmAction(title, text, confirmText = 'Yes, proceed', color = '#1479bd') {
  const res = await Swal.fire({ title, text, icon: 'question', showCancelButton: true, confirmButtonText: confirmText, confirmButtonColor: color, cancelButtonColor: '#6b7280' });
  return res.isConfirmed;
}

// Wrap an async action with busy state + toast
export function useAction() {
  const [busy, setBusy] = useState(null);
  const run = async (key, fn, success) => {
    setBusy(key);
    try {
      const r = await fn();
      if (success !== false) notify.success(success || r?.message || 'Done');
      return r;
    } catch (e) {
      notify.error(e.message || 'Something went wrong');
      return undefined;
    } finally {
      setBusy(null);
    }
  };
  return [busy, run];
}

// Simple CSV export
export function downloadCSV(filename, rows, columns) {
  const esc = (v) => {
    const s = v === undefined || v === null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map((c) => esc(c.label)).join(',')];
  rows.forEach((r) => lines.push(columns.map((c) => esc(typeof c.value === 'function' ? c.value(r) : r[c.value])).join(',')));
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

// Generic workspace request action bar (Approve / Return / Reject)
export function RequestDecision({ request, onDone, approveLabel = 'Approve', stageFields, compact }) {
  const [busy, run] = useAction();
  const [stageData, setStageData] = useState({});
  if (!request?.canAct) return null;
  const act = async (action) => {
    let remarks = '';
    if (action !== 'approve') {
      remarks = await promptText({ title: action === 'reject' ? 'Reject request' : 'Return for clarification', confirmText: action === 'reject' ? 'Reject' : 'Return', color: action === 'reject' ? '#c43c31' : '#1479bd' });
      if (remarks === null) return;
    }
    const r = await run(action, () => wsAPI.actRequest(request._id, { action, remarks, stageData: Object.keys(stageData).length ? stageData : undefined }),
      action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Returned for clarification');
    if (r && onDone) onDone(r.request);
  };
  return (
    <div className="tse-stack" style={{ gap: 8 }}>
      {!compact && <Note tone="info">Review the details and take an action.</Note>}
      {stageFields && stageFields(stageData, (k, v) => setStageData((p) => ({ ...p, [k]: v })))}
      <button type="button" className="tse-btn navy block" style={{ justifyContent: 'flex-start' }} disabled={!!busy} onClick={() => act('approve')}>{busy === 'approve' ? <Spin /> : <i className="bi bi-check-lg" />}{approveLabel}</button>
      <button type="button" className="tse-btn outline block" style={{ justifyContent: 'flex-start' }} disabled={!!busy} onClick={() => act('return')}><i className="bi bi-chat-left-text" />Return for clarification</button>
      <button type="button" className="tse-btn danger-outline block" style={{ justifyContent: 'flex-start' }} disabled={!!busy} onClick={() => act('reject')}><i className="bi bi-x-lg" />Reject</button>
    </div>
  );
}

// Workspace request status label
export function reqStatusLabel(r) {
  if (!r) return 'Draft';
  if (r.status === 'PENDING') return r.currentStageLabel ? `Pending — ${r.currentStageLabel}` : 'Pending';
  return titleCase(r.status);
}
export function ReqStatus({ request }) {
  const s = request?.status || 'DRAFT';
  const tone = { PENDING: 'pending', APPROVED: 'approved', CLOSED: 'approved', REJECTED: 'rejected', RETURNED: 'rejected', DRAFT: 'draft', CANCELLED: 'draft' }[s];
  const label = s === 'PENDING' ? (request.currentStageLabel ? `${request.currentStageLabel}` : 'Pending') : s === 'RETURNED' ? 'Returned' : titleCase(s);
  return <Badge tone={tone}>{label}</Badge>;
}

export const BUSINESS_UNITS = ['Water Business Unit', 'Industrial Business Unit', 'Corporate', 'Water Management Services'];
export const DIVISIONS = ['Engineering & Constructions', 'Water Management Services', 'Operations & Maintenance', 'Corporate Services'];
export const LOCATIONS = ['Jamshedpur', 'New Delhi', 'West Bokaro', 'Korba', 'Jajpur', 'Kolkata', 'Ranchi', 'Raipur'];
export const DEPARTMENTS = ['Water Business Unit', 'Engineering', 'Procurement', 'Finance', 'HR', 'Administration', 'IT', 'EHS', 'Operations'];
